import {
  ProviderUnavailableError,
  type ImageGenRequest,
  type ImageProvider,
  type ProviderCallOptions,
  type ProviderImageJobState,
  type ProviderJobHandle,
} from "@loumy/contracts";

// §7 `image`-Provider → **Qwen Image 2.0** über den fal.ai-Aggregator (D63, löst D62/GPT Image 2
// ab). Bezieht das Modell über die fal.ai-Queue-API mit DEMSELBEN `FAL_API_KEY` wie video/vision
// (1 Key, kein separater Vertrag). ECHTER Adapter, ENV-gated (ohne Key → ProviderUnavailableError).
//
// D27 Async-Resume: `generate()` SUBMITtet nur (POST) und liefert ein Handle (request_id +
// status/response-URL); `poll(handle)` macht GENAU EINEN Status-Check (KEIN interner Loop mehr).
// Der Loop + das Pausieren/Resumen lebt im Treiber `runImageProviderJob` (provider-poll.ts) →
// ein langsamer fal-Job pausiert und pollt in einem späteren Tick DASSELBE Handle weiter
// (kein zweiter Submit, keine Doppel-Kosten).
//
// Contract (fal.ai/models/fal-ai/qwen-image-2/text-to-image/api, verifiziert 2026-06):
//   Auth   : Header `Authorization: Key <FAL_API_KEY>`.
//   Submit : POST {queueBase}/fal-ai/qwen-image-2/text-to-image (JSON) → { request_id, status_url, response_url }
//   Status : GET  {status_url}  → { status: IN_QUEUE|IN_PROGRESS|COMPLETED, error? }
//   Result : GET  {response_url} → { images: [{ url, ... }], seed }
//   Wir nutzen die VON fal zurückgegebenen status_url/response_url (host-validiert) statt sie selbst zu bauen.

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_QWEN_IMAGE_MODEL = "fal-ai/qwen-image-2/text-to-image";
// D76: Qwen 2.0 = generate UND edit in EINEM Modell (User). Bei Referenzbildern → EDIT-Endpoint
// (`image_urls`, 1–3 Bilder) statt text-to-image. Gleicher FAL_API_KEY, gleiche Auflösungs-/
// Megapixel-Metrik (image_size) ⇒ KEIN Pricing-Drift. Löst das frühere maxRefs=0-TODO (§13).
const FAL_QWEN_EDIT_MODEL = "fal-ai/qwen-image-2/edit";

// Deterministische Auflösung je Aspect-Ratio (§10.2). Im Build eingefroren (D30) → Megapixel-
// Pricing; generate erzeugt exakt diese Dimensionen. Identisch zu D62 (alle durch 64 teilbar,
// Gesamtpixel innerhalb Qwens 512²–2048²-Bereich) ⇒ KEIN Pricing-Drift beim Provider-Wechsel.
const ASPECT_TO_RESOLUTION: Record<string, string> = {
  "1:1": "1024x1024",
  "3:2": "1152x768",
  "2:3": "768x1152",
  "16:9": "1344x768",
  "9:16": "768x1344",
};

export interface FalQwenImageConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Endpoint. Default `fal-ai/qwen-image-2/text-to-image` (Standard); für den
   *  Pro-Endpoint `fal-ai/qwen-image-2/pro/text-to-image` setzen (höhere COGS, §13). */
  model?: string;
  /** D76: Edit-Endpoint-Override (Default `fal-ai/qwen-image-2/edit`) — bei Referenzbildern. */
  editModel?: string;
  /** Qwens serverseitige Prompt-Expansion. Default `false` — wir veredeln den Prompt selbst
   *  über den `llm`-Slot (`prompt-enhance`, D60); doppelte Expansion vermeiden. */
  promptExpansion?: boolean;
  /** Qwens Safety-Checker. Default `true` (fail-safe). */
  safetyChecker?: boolean;
}

/** Im `raw` des Handles persistierte Resume-Felder (D27): die fal-URLs + die eingefrorene Auflösung. */
interface FalImageHandleRaw {
  statusUrl: string;
  responseUrl: string;
  resolution: string;
  /** D76: tatsächlich genutzter Endpoint (text-to-image vs. edit) — für die Provenance (D39). */
  model: string;
}

export class FalQwenImageProvider implements ImageProvider {
  readonly id = "fal-qwen-image";
  readonly version = 1;
  readonly displayName = "fal.ai (Qwen Image 2.0)";
  readonly limits = {
    supportedResolutions: Object.values(ASPECT_TO_RESOLUTION),
    // D76: Qwen-Edit-Endpoint nimmt `image_urls` mit 1–3 Bildern ⇒ Referenz-Conditioning aktiv.
    maxRefs: 3,
    supportsGreenscreenDirective: true, // prompt-getrieben (wie video, D55)
  };

  private readonly baseUrl: string;
  private readonly model: string;
  private readonly editModel: string;
  private readonly promptExpansion: boolean;
  private readonly safetyChecker: boolean;

  constructor(private readonly config: FalQwenImageConfig = {}) {
    // Leere Env-Strings (`FAL_BASE_URL=`) als „nicht gesetzt" behandeln ⇒ Default greift.
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_QWEN_IMAGE_MODEL;
    this.editModel = config.editModel?.trim() || FAL_QWEN_EDIT_MODEL;
    this.promptExpansion = config.promptExpansion ?? false;
    this.safetyChecker = config.safetyChecker ?? true;
  }

  /** deterministisch; im Build eingefroren (D30). */
  resolve(aspectRatio: string): { resolution: string } {
    return { resolution: ASPECT_TO_RESOLUTION[aspectRatio] ?? ASPECT_TO_RESOLUTION["1:1"]! };
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("image", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /**
   * GET im Poll: ok → Response; 429/5xx/Netzwerkabbruch → null (TRANSIENT → der Treiber pollt
   * später erneut, D27); übrige 4xx → terminaler Fehler (Auth/Contract). Wie fal.ts (D27).
   */
  private async pollGet(url: string, apiKey: string, op: string, signal?: AbortSignal): Promise<Response | null> {
    let res: Response;
    try {
      res = await fetch(url, { method: "GET", headers: this.headers(apiKey, false), signal });
    } catch {
      return null; // Netzwerkabbruch / Abbruch → transient (der Treiber prüft signal.aborted)
    }
    if (res.ok) return res;
    if (res.status === 429 || res.status >= 500) return null; // transient
    const body = (await res.text().catch(() => "")).slice(0, 500); // D119: 4xx-Grund mitgeben
    throw new Error(`fal qwen-image ${op}: HTTP ${res.status} ${res.statusText}${body ? ` — ${body}` : ""}`);
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift, wie fal.ts) — auch beim Resume. */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal qwen-image: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal qwen-image: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal qwen-image: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /** D27: Submit (POST) → Handle (request_id + status/response-URL + eingefrorene Auflösung). KEIN Poll. */
  async generate(req: ImageGenRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    // D76: Referenzbilder → EDIT-Endpoint (Qwen 2.0 = generate+edit in EINEM Modell). 1–3 Bilder.
    const refs = req.referenceUrls ?? [];
    const useEdit = refs.length > 0;
    if (refs.length > this.limits.maxRefs)
      throw new ProviderUnavailableError(
        "image",
        this.id,
        `qwen-image-2/edit nimmt 1–${this.limits.maxRefs} Referenzbilder, ${refs.length} übergeben.`,
      );

    const { resolution } = this.resolve(req.aspectRatio);
    const parts = resolution.split("x");
    const width = Number(parts[0]);
    const height = Number(parts[1]);
    const count = Math.max(1, Math.min(4, Math.round(req.count)));
    // Greenscreen = prompt-getrieben (Alpha entsteht im server-GPU `chroma-key`, D45/D55).
    const prompt = req.greenscreen
      ? `${req.prompt}\n\nThe subject is on a solid chroma-key green (#00FF00) background, evenly lit, no shadows on the background.`
      : req.prompt;

    // Edit-Body (image_urls) bzw. text-to-image-Body — gleiche Auflösungs-/Megapixel-Metrik.
    const body: Record<string, unknown> = {
      prompt,
      image_size: { width, height },
      num_images: count,
      output_format: "png",
      enable_prompt_expansion: this.promptExpansion,
      enable_safety_checker: this.safetyChecker,
    };
    if (useEdit) body["image_urls"] = refs;

    const submit = await fetch(`${this.baseUrl}/${useEdit ? this.editModel : this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify(body),
      signal: opts?.signal,
    });
    if (!submit.ok) {
      // D119-Diagnose-Härtung: bei 4xx den fal-Antwort-Body (gekappt) anhängen — ein nacktes
      // „HTTP 403 Forbidden" verschleiert den ECHTEN Grund (z.B. Modell-Zugriffs-/Spend-Gate,
      // fehlendes Zahlungsmittel). Bei 5xx KEIN Body-Read (transienter Server-Fehler, Body selten
      // nützlich) — der Submit failt aber bei JEDEM !ok, wie im fal-bria/-vace/-ben2-Muster.
      const body = submit.status < 500 ? (await submit.text().catch(() => "")).slice(0, 500) : "";
      throw new Error(`fal qwen-image submit: HTTP ${submit.status} ${submit.statusText}${body ? ` — ${body}` : ""}`);
    }
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal qwen-image submit: keine request_id in der Antwort.");
    const raw: FalImageHandleRaw = {
      statusUrl: this.requireFalUrl(sub.status_url, "status_url"),
      responseUrl: this.requireFalUrl(sub.response_url, "response_url"),
      resolution,
      model: useEdit ? this.editModel : this.model,
    };
    return { providerId: this.id, providerVersion: this.version, externalId: sub.request_id, raw };
  }

  /** D27: EIN Status-Check des Handles. IN_QUEUE/IN_PROGRESS → processing; COMPLETED → done; error → failed. */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageJobState> {
    const apiKey = this.requireKey();
    const raw = (handle.raw ?? {}) as Partial<FalImageHandleRaw>;
    // Beim Resume (anderer Tick/Instanz) die URLs aus dem persistierten Handle erneut host-
    // validieren (Defense-in-Depth gegen manipuliertes raw, SSRF).
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url");
    const resolution = typeof raw.resolution === "string" ? raw.resolution : "1024x1024";

    const statusRes = await this.pollGet(statusUrl, apiKey, "status", opts?.signal);
    if (!statusRes) return { status: "processing" }; // transient → später erneut pollen
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: status.error };
    if (status.status !== "COMPLETED") return { status: "processing" }; // IN_QUEUE / IN_PROGRESS

    const resultRes = await this.pollGet(responseUrl, apiKey, "result", opts?.signal);
    if (!resultRes) return { status: "processing" }; // Result transient → weiterpollen (Status bleibt COMPLETED)
    const result = (await resultRes.json()) as { images?: { url?: unknown }[]; seed?: unknown };
    const images = (result.images ?? [])
      .filter((i): i is { url: string } => typeof i?.url === "string")
      .map((i) => ({ url: i.url }));
    if (images.length === 0) return { status: "failed", error: "fal qwen-image: COMPLETED, aber keine images im Ergebnis." };
    return {
      status: "done",
      output: {
        images,
        resolution,
        provenance: {
          provider: this.id,
          modelVersion: typeof raw.model === "string" ? raw.model : this.model,
          requestId: handle.externalId,
          // Qwen liefert den genutzten Seed zurück → echte Provenance (D39); sonst null.
          seed: typeof result.seed === "number" ? result.seed : null,
        },
      },
    };
  }
}
